/* eslint-disable react/prop-types */
import React, { useEffect } from 'react'
import { getValue } from 'remote-config-value'
import { minlength, regex, required } from 'validation-rules'
import { makeStyles } from '@material-ui/core/styles'
import { CountrySelector } from 'functional-foundations'
import { Alert, Select, TextField, SearchableInput } from 'foundations-library/components'
import StepTitle from '../../shared/StepTitle'
import useFlatForm from '../../../hooks/useFlatForm'
import useDisableInput from '../../../hooks/useDisableInput'
import { countryOptions, verticalOptions, businessTypeOptions } from '../../../utils/util'

const PUBLIC_PROFIT = 'PUBLIC_PROFIT'

const styles = makeStyles((theme) => ({
    businessInformation: {
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px'
    },
    twoInputsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px'
    },
    stockField: {
        margin: '2px 0'
    }
}))

const availableCountries = countryOptions.map((x) => x.value)

/**
 * @param {object} props
 */
const BusinessInformationStep = (props) => {
    const classes = styles()
    const bizInfoSavedData = props?.passData?.business_info
    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp,
        isFormDataValid
    } = useFlatForm({
        brandName: bizInfoSavedData ? bizInfoSavedData.brandName : (props.editing != null ? props.editing.name : null),
        legalBizName: bizInfoSavedData ? bizInfoSavedData.legalBizName : (props.editing != null ? props.editing['contact.name'] : null),
        registrationCountry: bizInfoSavedData ? bizInfoSavedData.registrationCountry : (props.editing != null ? props.editing.country : 'US'),
        federalBusinessID: bizInfoSavedData ? bizInfoSavedData.federalBusinessID : (props.editing != null ? props.editing.business_identifier : null),
        selectedBizType: bizInfoSavedData ? bizInfoSavedData.selectedBizType : (props.editing != null ? props.editing.business_type_name : null),
        selectedVerticle: bizInfoSavedData ? bizInfoSavedData.selectedVerticle : (props.editing != null ? props.editing.business_vertical_name : null),
        stockSymbol: bizInfoSavedData ? bizInfoSavedData.stockSymbol : (props.editing != null ? props.editing.stock_symbol : null)
    })
    const { disableAllInputs } = useDisableInput({ mode: props.mode, item: props.editing })

    // const disabledField = props?.editing != null && (props.editing.registration_status === 'approved' || props.editing.campaign_count > 0)

    useEffect(() => {
        props?.update?.({
            disableNextButton: () => !isFormDataValid
        })
    }, [isFormDataValid, data.selectedBizType])

    useEffect(() => {
        if (data.selectedBizType !== PUBLIC_PROFIT) {
            setDataProp('stockSymbol')('')
            setTimeout(() => {
                setIsValidProp('stockSymbol')(true)
            }, 1) // prevents react behavior
        } else {
            setIsValidProp('stockSymbol')(!!data.stockSymbol)
        }
    }, [data.selectedBizType])

    // Runs when the next button is clicked
    useEffect(() => {
        if (props.saveId) props?.update?.({ completed: true, gotoNext: true, saveData: data })
    }, [props.saveId])

    return <div className={classes.businessInformation} data-testid='business-info-step'>
        <StepTitle
            remoteConfigID='sms_enrollment_business_information'
            mode={props?.mode}
            status={props?.editing?.registration_status}
            onClickEdit={props.onClickEdit}
            onClickSubmitPaymentFailed={props.onClickSubmitPaymentFailed}
        />
        <div className={classes.form}>
            <TextField
                fullWidth={true}
                label={getValue('sms_enrollment_brand_name')}
                value={data.brandName}
                rules={[
                    required(),
                    minlength(3)
                ]}
                onChange={setDataProp('brandName')}
                isValid={setIsValidProp('brandName')}
                onClear={onClear('brandName')}
                helperText={getValue('sms_enrollment_brand_name_helper_text')}
                disabled={disableAllInputs}
                data-testid='business-info-step-brand-name'
            />
            <div className={classes.twoInputsContainer}>
                <TextField
                    fullWidth={true}
                    label={getValue('sms_enrollment_business_name')}
                    value={data.legalBizName}
                    onChange={setDataProp('legalBizName')}
                    onClear={onClear('legalBizName')}
                    rules={[required()]}
                    isValid={setIsValidProp('legalBizName')}
                    helperText={getValue('sms_enrollment_business_name_helper_text')}
                    disabled={disableAllInputs}
                    data-testid='business-info-step-legal-bus-name'
                />
                <Select
                    fullWidth={true}
                    value={data.selectedBizType}
                    options={businessTypeOptions.map(x => ({ value: x.value, text: x.label }))}
                    onChange={setDataProp('selectedBizType')}
                    rules={[required()]}
                    isValid={setIsValidProp('selectedBizType')}
                    label={getValue('sms_enrollment_business_type')}
                    disabled={disableAllInputs}
                    data-testid='business-info-step-biz-type'
                />

            </div>
            {data.selectedBizType === PUBLIC_PROFIT && <TextField
                fullWidth
                label={getValue('sms_enrollment_stock_symbol')}
                value={data.stockSymbol}
                onChange={setDataProp('stockSymbol')}
                rules={[required()]}
                isValid={setIsValidProp('stockSymbol')}
                onClear={onClear('stockSymbol')}
                disabled={disableAllInputs}
                className={classes.stockField}
                data-testid='business-info-step-stock-symbol'
            />}
            <Alert level='warning' data-testid='business-info-step-name-alert'>
                {getValue('sms_enrollment_business_name_alert')}
            </Alert>
            <TextField
                fullWidth={true}
                inputMode='numeric'
                maxLength={9}
                label={
                    data.registrationCountry === 'CA'
                        ? getValue('sms_enrollment_business_number')
                        : getValue('sms_enrollment_federal_business_id')
                }
                value={data.federalBusinessID}
                onChange={setDataProp('federalBusinessID')}
                onClear={onClear('federalBusinessID')}
                rules={[required(), regex('^\\d{9}$', undefined, 'This field must be 9 digits number')]}
                isValid={setIsValidProp('federalBusinessID')}
                helperText={getValue('sms_enrollment_tax_id_ein_helper_text')}
                disabled={disableAllInputs}
                data-testid='business-info-step-buz-id'
            />
            <div className={classes.twoInputsContainer}>
                <SearchableInput
                    options={verticalOptions.map((x) => ({ text: x.displayName, value: x.id }))}
                    fullWidth={true}
                    label={getValue('sms_enrollment_industry_vertical')}
                    value={data.selectedVerticle}
                    rules={[required()]}
                    isValid={setIsValidProp('selectedVerticle')}
                    onChange={setDataProp('selectedVerticle')}
                    disabled={disableAllInputs}
                    data-testid='business-info-step-verticle'
                />
                <CountrySelector
                    availableCountries={availableCountries}
                    fullWidth={true}
                    label={getValue('sms_enrollment_country_of_registration')}
                    value={data.registrationCountry}
                    onChange={setDataProp('registrationCountry')}
                    rules={[required()]}
                    isValid={setIsValidProp('registrationCountry')}
                    disabled={disableAllInputs}
                    data-testid='business-info-step-country'
                />
            </div>
        </div>

    </div>
}

export default BusinessInformationStep
